import React, { useMemo, useRef, useEffect } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { gsap } from 'gsap';
import vertexShader from './shaders/vertex.glsl';
import fragmentShader from './shaders/fragment.glsl';

interface ParticlesProps {
  data: Uint8ClampedArray;
  width: number;
  height: number;
  onComplete: () => void;
  targetBtn?: HTMLButtonElement;
}

const Particles: React.FC<ParticlesProps> = ({ data, width, height, onComplete, targetBtn }) => {
  const pointsRef = useRef<THREE.Points>(null);

  const { positions, colors, destinations, speeds } = useMemo(() => {
    const step = 4;
    const pos = [];
    const col = [];
    const dest = [];
    const spd = [];

    for (let y = 0; y < height; y += step) {
      for (let x = 0; x < width; x += step) {
        const i = (y * width + x) * 4;

        pos.push(x / 10 - width / 20, -y / 10 + height / 20, 0);

        col.push(data[i] / 255, data[i + 1] / 255, data[i + 2] / 255);

        dest.push(
          (x / 10 - width / 20) + (Math.random() - 0.5) * 50,
          (-y / 10 + height / 20) + (Math.random() - 0.5) * 50,
          (Math.random() - 0.5) * 50
        );

        spd.push(0.5 + Math.random() * 1.05);
      }
    }
    return {
      positions: new Float32Array(pos),
      colors: new Float32Array(col),
      destinations: new Float32Array(dest),
      speeds: new Float32Array(spd),
    };
  }, [data, width, height]);

  const particleCount = positions.length / 3;

  const uniforms = useMemo(() => ({
    uProgress: { value: 0 },
    uTime: { value: 0 },
    uFlash: { value: 0 }
  }), []);

  useEffect(() => {
    const tl = gsap.timeline();

    if (targetBtn) {
      targetBtn.style.visibility = 'hidden';
    }

    tl.to(uniforms.uFlash, {
      value: 0.5,
      duration: 0.1
    }).to(uniforms.uFlash, {
      value: 0,
      duration: 0.4
    });
    
    tl.to(uniforms.uProgress, {
      value: 1,
      duration: 5,
      ease: "power3.out",
      onComplete: onComplete
    }, 0);

    tl.to(uniforms.uTime, {
      value: 100,
      duration: 1,
      repeat: -1,
      ease: "none"
    }, 0);
    return () => {
      tl.kill();
    };
  }, [uniforms, onComplete, targetBtn]);

  useFrame((state) => {
    uniforms.uTime.value = state.clock.getElapsedTime();
  });

  return (
    <points ref={pointsRef}>
      <bufferGeometry>
        <bufferAttribute
          attach="attributes-position"
          count={particleCount}
          array={positions}
          itemSize={3}
        />
        <bufferAttribute
          attach="attributes-color"
          count={particleCount}
          array={colors}
          itemSize={3}
        />
        <bufferAttribute
          attach="attributes-destination"
          count={particleCount}
          array={destinations}
          itemSize={3}
        />
        <bufferAttribute
          attach="attributes-aSpeed"
          count={speeds.length}
          array={speeds}
          itemSize={1}
        />
      </bufferGeometry>
      <shaderMaterial
        vertexShader={vertexShader}
        fragmentShader={fragmentShader}
        uniforms={uniforms}
        transparent
        depthTest={false}
        blending={THREE.AdditiveBlending}
      />
    </points>
  );
};

export default Particles;